import { User } from "./user";
import * as _ from 'lodash';
import { TicketComment } from "./ticket-comment";
import { TicketStatusLog } from "./ticket-status-log";
import { Validable } from "./common";
import { Attachment } from "./attachment";
import { Client } from "./client";
import * as moment from 'moment'
import { TicketType } from "./ticket-type";

const SAVE_PROPS = ['title','description','assign_to_id','client_id', 'status_id','ticket_comments_attributes', 'new_status_id','attachments','receipt_id', 'ticket_type_id'];

export class Ticket extends Validable{
  validationKey = 'ticket';
  title: string;
  description: string;
  assign_to_id: number;
  created_by_id: number;
  closed_by_id: number;
  client_id: number;
  duration: number;
  receipt_id: string;
  ticket_type_id: number;
  attachments: Attachment[]; // Links to downloads are contained in the objects
  client: Client;
  ticket_comments: {[id:number]: TicketComment};
  ticket_status_logs: TicketStatusLog[];
  created_by: User;
  closed_by: User;
  warning: number[];
  private _status_id: number;
  private _assign_to: User;
  private _ticket_type: TicketType;
  private _subreceipt_id: string;

  static canAssignTicket(user: User, ticket: Ticket) {
    if (user.isMaterialist || user.isSupervisor || user.isAdmin)
      return Ticket.internalCanEdit(user, ticket);
    else
      return false;
  }

  static canEditTicket(user: User, ticket: Ticket) {
    if (user.isMaterialist || user.isSupervisor || user.isAdmin)
      return Ticket.internalCanEdit(user, ticket);
    else {
      const created_by = _.get(ticket, 'created_by_id', null);
			const assign_to = _.get(ticket, 'assign_to_id', null);
			return (user.id == created_by || user.id == assign_to) && ["O","R"].includes(ticket.status_key);
    }
  }

  static internalCanEdit(user: User, ticket: Ticket) {
    if (ticket.status_key == "C") {
      return user.isSupervisor || user.isAdmin;
    } else {
      return this.canViewTicket(user, ticket);
    }
  }

  static canViewTicket(user: User, ticket: Ticket) {
    if(user.isMaterialist || user.isAdmin) {
			const client_id = _.get(ticket, 'client_id', null);
			return user.client_ids.includes(client_id);
		} else if(user.isSupervisor) {
			const created_by = _.get(ticket, 'created_by', null);
			return user.warehouse_id == created_by.warehouse_id;
		} else {
			const created_by = _.get(ticket, 'created_by_id', null);
			const assign_to = _.get(ticket, 'assign_to_id', null);
			return user.id == created_by || user.id == assign_to;
		}
  }

  constructor(data) {
    super(data);
    this.setup_comments(data.ticket_comments);
    this.setup_defaults();
  }


  get ticket_comments_attributes(): TicketComment[] {
    return _.values(this.ticket_comments);
  }

  private setup_defaults() {
    if (!this.ticket_comments) {
      this.ticket_comments = [];
    }

    if (!this.attachments) {
      this.attachments = [];
    } else {
      this.attachments = this.attachments.map(a => new Attachment(a));
    }

    if (this.ticket_status_logs){
      this.ticket_status_logs = this.ticket_status_logs.map(s => new TicketStatusLog(s));
    }

    if (this.created_by && !(this.created_by instanceof User)) {
      this.created_by = new User(this.created_by);
    }

    if (this.closed_by && !(this.closed_by instanceof User)) {
      this.closed_by = new User(this.closed_by);
    }

    if (this.client && !(this.client instanceof Client)) {
      this.client = new Client(this.client);
    }
    if (this.client) this.client_id = this.client.id;

    if (this.assign_to && !(this.assign_to instanceof User)) {
      this.assign_to = new User(this.assign_to);
    }

    if (this.ticket_type && !(this.ticket_type instanceof TicketType))
      this.ticket_type = new TicketType(this.ticket_type);

    if (this.receipt_id){
      let parts = this.receipt_id.split("-");
      this._subreceipt_id = `${parts[1]}-${parts[2]}`;
    }

    this.ticket_comments = this.ticket_comments || {};
  }

  private setup_comments(comments) {
    this.ticket_comments = _(comments).map((c) => new TicketComment(c)).keyBy('id').value();
  }


  get status_id(){
    if (!this._status_id){
      this._status_id = this.ticket_status  ? this.ticket_status.id : null;
    }
    return this._status_id;
  }

  set status_id(value: number) {
    this._status_id = value;
  }


  get ticket_status() {
    if (this.ticket_status_logs){
      let active = this.ticket_status_logs.filter(s => s.active === true)[0] || this.ticket_status_logs[0];
      if (active) return active.ticket_status;
    }
    return null;
  }

  get status_key() {
    if (this.ticket_status)
      return this.ticket_status.key;
    return '';
  }

  set assign_to(value) {
    if (value instanceof Object) {
      this.assign_to_id = value.id;
      this._assign_to = value;
    }
  }

  get assign_to() {
    return this._assign_to;
  }

  get saveProp(): string[] {
    return super.saveProp.concat(SAVE_PROPS);
  }

  toSaveData() {
    const data = super.toSaveData();

    const hasTicketAttachments = _(this.attachments).size() > 0 && _.every(this.attachments,(a) => a.file != null && a.file != undefined);
    const hasCommentAttachments = _(this.ticket_comments_attributes).some(comment => 
      comment.attachments && comment.attachments.length > 0 && 
      _.some(comment.attachments, a => a.file != null && a.file != undefined)
    );
    
    if (hasTicketAttachments || hasCommentAttachments) {
      let formData = new FormData();
      
      this.attachments.forEach(a => {
        if (!a.stored){
          formData.append(`ticket[attachments][][file]`,a.file,a.filename);
          formData.append(`ticket[attachments][][filename]`,a.filename);
        } else if (a._destroy) {
          formData.append(`ticket[attachments][][id]`,a.id.toString());
          formData.append(`ticket[attachments][][_destroy]`,a._destroy.toString());
        }
      });

      this.ticket_comments_attributes.forEach((comment, commentIndex) => {
        formData.append(`ticket[ticket_comments_attributes][${commentIndex}][comment]`, comment.comment || '');
        if (comment.id) {
          formData.append(`ticket[ticket_comments_attributes][${commentIndex}][id]`, comment.id.toString());
        }
        if (comment.created_by_id) {
          formData.append(`ticket[ticket_comments_attributes][${commentIndex}][created_by_id]`, comment.created_by_id.toString());
        }
        
        if (comment.attachments && comment.attachments.length > 0) {
          comment.attachments.forEach(attachment => {
            if (!attachment.stored) {
              formData.append(`ticket[ticket_comments_attributes][${commentIndex}][attachments][][file]`, attachment.file, attachment.filename);
              formData.append(`ticket[ticket_comments_attributes][${commentIndex}][attachments][][filename]`, attachment.filename);
            } else if (attachment._destroy) {
              formData.append(`ticket[ticket_comments_attributes][${commentIndex}][attachments][][id]`, attachment.id.toString());
              formData.append(`ticket[ticket_comments_attributes][${commentIndex}][attachments][][_destroy]`, attachment._destroy.toString());
            }
          });
        }
      });

      _(data).forEach((v:any,k: string) => {
        if (v !== undefined && v !== null && k !== 'attachments' && k !== 'ticket_comments_attributes'){
          formData.append(`ticket[${k}]`, v);
        }
      });

      return formData;
    }
    return {'ticket': data};
  }

  get has_attachments(): boolean {
    return _.size(this.attachments) > 0;
  }

  get duration_human(): string {
    return moment.duration(this.duration, 'seconds').humanize();
  }


  removeNotPersistedAttachments() {
    this.attachments = this.attachments.filter(e => e.stored === false);
  }

  appendAttachment(a: Attachment) {
    this.attachments.push(a);
  }

  appendAttachmentFromFile(file: File) {
    this.attachments.push(new Attachment({
      file: file,
      filename: file.name
    }));
  }


  get ticket_type(): TicketType {
    return this._ticket_type;
  }

  set ticket_type(value: TicketType) {
    this._ticket_type = value;
    this.ticket_type_id = value ? value.id : null;
  }

  validate() {
    super.validate();
    if (this.ticket_type && this.ticket_type.require_receipt && !this.receipt_id)
      this.errors["receipt_id"] = "Required";
  }

  get warning_txt(): string {
    return this.warning && this.warning.length > 0 ? this.warning.map(t => '#'+t).join(", ") : null;
  }

  get subreceipt_id(): string{
    return this._subreceipt_id;
  }

  set subreceipt_id(value: string){
    this.receipt_id = this.client.part_code + value;
    this._subreceipt_id = value;
  }

  get composeTitle(): string {
    return `#${this.id} ${this.ticket_type.name}`;
  }

  get commentAttachments(): Attachment[] {
    let attachments: Attachment[] = [];
    _.values(this.ticket_comments).forEach(c => {
      attachments = attachments.concat(c.attachments);
    });
    return attachments;
  }

  get isNew(): boolean {
    return this.id === null || this.id === undefined || this.id <= 0;
  }

}
